import { normalizeTags } from 'features/datasets/utils/tags';

/** The fields a row filter can look at beyond the input columns. */
export const ANY_FIELD = '__any';

export const FILTER_OPS = [
  { value: 'is', label: 'Is' },
  { value: 'is_not', label: 'Is not' },
  { value: 'contains', label: 'Contains' },
  { value: 'does_not_contain', label: 'Does not contain' },
  { value: 'is_set', label: 'Is set' },
  { value: 'is_not_set', label: 'Is not set' },
];
export const VALUELESS = new Set(['is_set', 'is_not_set']);

/** The search bar's filter. The grid adds it to the list; the server reads it as a search over every column. */
export const SEARCH_FIELD = '__search';

/** The columns beyond the input variables that a filter can look at. */
const FIXED_FIELDS = [
  { value: '__links', label: 'Links' },
  { value: '__input', label: 'Input' },
  { value: '__output', label: 'Output' },
  { value: '__expected', label: 'Expected' },
  { value: '__evaluations', label: 'Evaluations' },
  { value: '__metadata', label: 'Metadata' },
  { value: '__created', label: 'Created' },
  { value: '__updated', label: 'Updated' },
];

/** Filterable fields: the grid's optional columns, the same ones the Columns menu lists. */
export function filterFields() {
  return FIXED_FIELDS;
}

const text = (v) => (v == null ? '' : String(v));

/** A value as the grid shows it: a string as itself, anything else as JSON. Empty arrays and objects are blank. */
function display(v) {
  if (v == null) return '';
  if (typeof v === 'string') return v;
  if (Array.isArray(v) && !v.length) return '';
  if (typeof v === 'object' && !Object.keys(v).length) return '';
  try { return JSON.stringify(v); } catch { return String(v); }
}

/** Every id and label a row's Links column points at. */
function linkValues(row) {
  const out = [];
  const walk = (v) => {
    if (v == null) return;
    if (typeof v === 'object') Object.values(v).forEach(walk);
    else out.push(String(v));
  };
  walk(row.__unit);
  walk(row.__origin);
  return out;
}

/** The Input cell as shown: `key: value` per non-blank variable, then `role: text` per turn, whitespace squashed. */
function inputText(row, columns) {
  const lines = columns.filter((c) => text(row[c]).trim() !== '').map((c) => `${c}: ${text(row[c]).trim()}`);
  for (const m of Array.isArray(row.__messages) ? row.__messages : []) {
    lines.push(`${text(m && m.role)}: ${typeof (m && m.content) === 'string' ? m.content : display(m && m.content)}`);
  }
  return lines.join(' ').replace(/\s+/g, ' ');
}

const FIXED_VALUES = {
  __links: (row) => linkValues(row),
  __input: (row, columns) => [inputText(row, columns)],
  __output: (row) => [display(row.__output)],
  __expected: (row) => [text(row.__expected)],
  __evaluations: (row) => [display(row.__evaluations)],
  __metadata: (row) => [text(row.__metadata)],
  __created: (row) => [text(row.__created)],
  __updated: (row) => [text(row.__updated)],
};

const allValues = (row, columns) => Object.values(FIXED_VALUES).flatMap((fn) => fn(row, columns));

/** The strings a row offers for a field. Tags are several; anything else is one. */
function valuesOf(row, field, columns) {
  if (field === ANY_FIELD) return [...allValues(row, columns), normalizeTags(row.__tags).join(' ')];
  if (field === SEARCH_FIELD) return [[...allValues(row, columns), ...normalizeTags(row.__tags)].join('\u001f')];
  if (field === '__tags') return normalizeTags(row.__tags);
  if (FIXED_VALUES[field]) return FIXED_VALUES[field](row, columns);
  return [text(row[field])];
}

export function matches(row, filter, columns) {
  const { field, operator } = filter;
  const raw = text(filter.value).trim().toLowerCase();
  const needle = field === '__input' ? raw.replace(/\s+/g, ' ') : raw;
  const vals = valuesOf(row, field, columns).map((v) => v.toLowerCase());
  const set = vals.some((v) => v.trim() !== '');
  switch (operator) {
    case 'is_set': return set;
    case 'is_not_set': return !set;
    case 'is': return vals.some((v) => v === needle);
    case 'is_not': return !vals.some((v) => v === needle);
    case 'does_not_contain': return !vals.some((v) => v.includes(needle));
    case 'contains':
    default: return vals.some((v) => v.includes(needle));
  }
}

/** A filter with no value yet (and one that needs a value) does nothing, so a half-built one never empties the grid. */
export const isActive = (f) => VALUELESS.has(f.operator) || text(f.value).trim() !== '';

export function applyRowFilters(rows, filters, columns) {
  const active = (filters || []).filter(isActive);
  if (!active.length) return rows;
  return rows.filter((r) => active.every((f) => matches(r, f, columns)));
}
